import toast from 'react-hot-toast';
import ToastAlert from '../components/ToastAlert';

interface ValidateImageFileOptions {
  maxFileSize: number;
  acceptedImageTypes: string[];
}

const FILE_TOO_LARGE_MESSAGE =
  "File is too big. Try one that's smaller than 5 MB";
const INVALID_FILE_TYPE_MESSAGE = 'Please upload a JPG or PNG file';

/**
 * Validates an image file for size and type
 * Shows toast error messages if validation fails
 * @param file - The file to validate
 * @param options - Validation options from GraphQL API
 * @returns true if valid, false if invalid
 */
export const validateImageFile = (
  file: File,
  options: ValidateImageFileOptions,
): boolean => {
  // Validate file size
  if (file.size > options.maxFileSize) {
    toast.custom(({ visible }) => (
      <ToastAlert
        isVisible={visible}
        level="error"
        message={FILE_TOO_LARGE_MESSAGE}
      />
    ));
    return false;
  }

  // Validate file type
  if (!options.acceptedImageTypes.includes(file.type)) {
    toast.custom(({ visible }) => (
      <ToastAlert
        isVisible={visible}
        level="error"
        message={INVALID_FILE_TYPE_MESSAGE}
      />
    ));
    return false;
  }

  return true;
};

/**
 * Reads a file and returns a data URL for preview
 * @param file - The file to read
 * @returns Promise that resolves with the data URL
 */
export const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
